import {
  useState,
  useEffect,
  useRef,
  useCallback,
  Dispatch,
  SetStateAction,
  MutableRefObject,
} from "react";

export interface FieldState<T = any> {
  editing: boolean;
  valid: boolean;
  value: T;
  touched: boolean;
}

export type UseFieldReturn<
  T = any,
  E extends HTMLElement = HTMLInputElement,
> = [
  FieldState<T>,
  Dispatch<SetStateAction<FieldState<T>>>,
  (canEdit: boolean) => void,
  MutableRefObject<E | null>,
  () => void,
];

const useField = <T = any, E extends HTMLElement = HTMLInputElement>(
  defaultValue: T,
  // `any` en el valor a validar, igual que el contrato original: tiparlo como `T`
  // lo convierte en un segundo punto de inferencia y compite con `defaultValue`.
  validate?: ((value: any) => boolean | Promise<boolean>) | null,
  focus?: boolean | null,
  isLoading?: boolean,
  readOnly?: boolean
): UseFieldReturn<T, E> => {
  const reference = useRef<E | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [field, setField] = useState<FieldState<T>>({
    editing: false,
    valid: true,
    value: defaultValue,
    touched: false,
  });

  const handleEdit = useCallback(
    (canEdit: boolean) => {
      setField((prev) => ({
        ...prev,
        editing: canEdit && !readOnly,
      }));
    },
    [readOnly]
  );

  const markTouched = useCallback(() => {
    setField((prev) => (prev.touched ? prev : { ...prev, touched: true }));
  }, []);

  useEffect(() => {
    if (!isLoading && !defaultValue) {
      handleEdit(true);
    }
  }, [isLoading, defaultValue, handleEdit]);

  useEffect(() => {
    setLoaded(false);
  }, [isLoading]);

  useEffect(() => {
    if (focus) {
      reference.current?.focus();
    }
  }, [focus]);
  useEffect(() => {
    setField((prev) => ({
      ...prev,
      value: defaultValue,
      editing:
        !isLoading && !readOnly && (defaultValue === "" || defaultValue == null)
          ? true
          : prev.editing,
      touched:
        defaultValue === "" || defaultValue == null ? false : prev.touched,
    }));
  }, [defaultValue, isLoading, readOnly]);

  useEffect(() => {
    if (!loaded) {
      setField((prev) => ({
        ...prev,
        value: defaultValue,
      }));
      setLoaded(true);
    }
  }, [loaded, defaultValue]);

  useEffect(() => {
    const applyValidation = (validationResult: boolean) => {
      const next = Boolean(validationResult);
      if (field.valid !== next) {
        setField((prev) => ({ ...prev, valid: next }));
      }
    };
    if (validate) {
      // El tipo va explícito: sin él, `Promise.resolve` deja el tipo del
      // validador sin desenvolver y `then` recibe `boolean | Promise<boolean>`.
      Promise.resolve<boolean>(validate(field.value)).then(applyValidation);
    }
  }, [field.value, field.valid, validate]);

  return [field, setField, handleEdit, reference, markTouched];
};

export default useField;
